import ConversationModel from 'Models/ConversationModel';

/**
 * Removes pinned conversations from a list of conversations.
 * @param conversations The list of all conversations.
 * @param pinnedConversations The list of conversations to remove.
 * @returns A new list of conversations without the pinned ones.
 */
export const removePinnedConversations = (
  conversations: ConversationModel[],
  pinnedConversations: ConversationModel[]
): ConversationModel[] => {
  return conversations.filter(
    (conversation) =>
      !pinnedConversations.some(
        (pinnedConversation) => pinnedConversation.id === conversation.id
      )
  );
};

/**
 * Removes duplicate conversations by ID (keeping the last occurrence)
 * @param conversations The list of conversations to deduplicate.
 * @returns A new list of conversations without duplicates.
 */
export const removeDuplicateConversations = (
  conversations: ConversationModel[]
): ConversationModel[] => {
  // Remove duplicates by ID, keeping the last occurrence
  const seenIds = new Set<string>();
  const deduplicatedConversations = conversations
    .reverse() // Reverse to process from end to start
    .filter((conversation) => {
      if (seenIds.has(conversation.id)) {
        return false; // Skip duplicates
      }
      seenIds.add(conversation.id);
      return true;
    })
    .reverse();

  return deduplicatedConversations;
};

/**
 * Removes duplicate conversations by ID (keeping the last occurrence) and sorts them
 * in descending order (most recent first) based on their last message date or creation date.
 * @param conversations The list of conversations to deduplicate and sort.
 * @returns A new list of conversations without duplicates, sorted by most recent first.
 */
export const sortAndRemoveDuplicatesConversations = (
  conversations: ConversationModel[]
): ConversationModel[] => {
  const deduplicatedConversations = removeDuplicateConversations(conversations);

  // Sort conversations in descending order (most recent first)
  return deduplicatedConversations.sort((a, b) => {
    const aDate = a.lastMessageDate || a.created;
    const bDate = b.lastMessageDate || b.created;
    return aDate <= bDate ? 1 : -1; // Descending order (most recent first)
  });
};

/**
 * Moves a conversation from one list to another.
 * @param from The source list of conversations.
 * @param to The destination list of conversations.
 * @param conversationId The ID of the conversation to move.
 * @returns An object with the updated 'from' and 'to' lists, or undefined if the conversation is not found in the "from" list.
 */
export const moveConversationBetweenLists = (
  from: ConversationModel[],
  to: ConversationModel[],
  conversationId: string
): { from: ConversationModel[]; to: ConversationModel[] } | undefined => {
  const conversationIndex = from.findIndex((c) => c.id === conversationId);

  if (conversationIndex === -1) {
    return undefined;
  }

  const updatedFrom = [...from];
  const [conv] = updatedFrom.splice(conversationIndex, 1);
  const updatedTo = [...to, conv];

  return {
    from: updatedFrom,
    to: updatedTo,
  };
};

/**
 * Returns the ID of the last conversation in a list of conversations.
 * @param conversations The list of conversations to get the last conversation ID from.
 * @returns The ID of the last conversation, or undefined if the list is empty.
 */
export const getLastConversationId = (
  conversations: ConversationModel[]
): string | undefined => {
  return conversations.length
    ? conversations[conversations.length - 1].id
    : undefined;
};
